import { motion } from "framer-motion";
import { Phone, Video, MoreVertical, Check } from "lucide-react";
import { CONSULTOR, WA_DEFAULT, WHATSAPP_DISPLAY } from "../../data/site";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";

const bubble = (delay) => ({
  initial: { opacity: 0, y: 14, scale: 0.96 },
  whileInView: { opacity: 1, y: 0, scale: 1 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
});

export const WhatsAppMockup = () => (
  <section id="contato" data-testid="whatsapp-mockup-section" className="overflow-hidden bg-paper py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
      <Reveal className="order-2 lg:order-1">
        <div className="relative mx-auto w-[300px] sm:w-[330px]">
          <div className="absolute -inset-6 rounded-[3.5rem] bg-champagne-light/60" />
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="relative rounded-[3rem] border border-white/10 bg-graphite p-3 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.8)]"
          >
            <div className="overflow-hidden rounded-[2.4rem] bg-[#ECE5DD]">
              <div className="flex items-center justify-between bg-[#075E54] px-5 py-4 text-white">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 font-display text-sm font-semibold">
                    LR
                  </span>
                  <div>
                    <p className="text-sm font-semibold leading-tight">{CONSULTOR.name}</p>
                    <p className="text-[10px] text-white/70">online</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-white/80">
                  <Video className="h-4 w-4" />
                  <Phone className="h-4 w-4" />
                  <MoreVertical className="h-4 w-4" />
                </div>
              </div>

              <div className="space-y-3 px-4 py-6" data-testid="mockup-chat">
                <motion.div {...bubble(0.3)} className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-[#DCF8C6] px-4 py-2.5 shadow-sm">
                  <p className="text-[13px] leading-snug text-slate-800">
                    Olá! Gostaria de receber uma cotação de plano de saúde.
                  </p>
                  <p className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-500">
                    agora <Check className="h-3 w-3 text-[#34B7F1]" />
                  </p>
                </motion.div>
                <motion.div {...bubble(1.1)} className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white px-4 py-2.5 shadow-sm">
                  <p className="text-[13px] leading-snug text-slate-800">
                    Perfeito! Vou entender seu perfil e apresentar as opções que
                    fazem sentido para você. Pode me contar um pouco do que procura?
                  </p>
                  <p className="mt-1 text-right text-[10px] text-slate-400">agora</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 2, duration: 0.4 }}
                  className="flex w-14 items-center justify-center gap-1 rounded-2xl rounded-tl-sm bg-white px-3 py-3 shadow-sm"
                >
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      animate={{ opacity: [0.25, 1, 0.25] }}
                      transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                      className="h-1.5 w-1.5 rounded-full bg-slate-400"
                    />
                  ))}
                </motion.div>
              </div>

              <div className="flex items-center gap-2 px-4 pb-5">
                <div className="flex-1 rounded-full bg-white px-4 py-2.5 text-[12px] text-slate-400 shadow-sm">
                  Mensagem
                </div>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#075E54] text-white">
                  <WhatsAppIcon className="h-5 w-5" />
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-champagne-dark">
            Contato direto
          </p>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Fale comigo pelo <em className="italic text-champagne-dark">WhatsApp</em>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-stone-400 sm:text-lg">
            Sem formulários complicados e sem espera. Você fala diretamente comigo,
            conta o que procura e recebe uma análise personalizada das opções
            disponíveis para o seu perfil.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-9 flex flex-col items-start gap-4">
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mockup-whatsapp-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-sm font-bold text-white shadow-[0_12px_40px_-10px_rgba(37,211,102,0.5)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-wa-dark"
          >
            <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-6" />
            Falar comigo pelo WhatsApp
          </a>
          <p className="font-mono text-sm font-semibold tracking-wide text-ink" data-testid="mockup-phone-number">
            {WHATSAPP_DISPLAY}
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
